import { useEffect, useMemo, useRef, useState } from "react";
import { QrCode, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSealPreview } from "@/hooks/useBoxTopper";
import { previewValues, sealCanvas, templateWithoutBackground, type ColorMode, type SealTemplate } from "@/utils/boxTopper";

const DOT = 300; // dots per inch: every CSS length below is `dots / 300 in`
const inches = (dots: number) => `${dots / DOT}in`;
const PLACEHOLDER = /\{([A-Za-z0-9_]+)\}/g;

interface SealPreviewProps {
  template: SealTemplate;
  colorMode: ColorMode;
  offerText: string;
  offerTerms: string;
}

/**
 * Two previews at the label's real size in the template's own orientation
 * (a portrait strip is 2.32 in wide x 6 in tall; an older landscape label the
 * reverse), in CSS inches:
 *  - an instant layout sketch drawn here, where red ink shows black whenever
 *    the label is set to black only, exactly as the printer will flatten it;
 *  - the relay's own render (`dry_run: true`), the pixel-true PNG, refreshed a
 *    moment after the last edit. It prints nothing.
 * A portrait strip is narrow enough for a phone, so the two sit side by side
 * when there is room and wrap otherwise; a landscape label is wider than a
 * phone, so each sits in its own horizontal scroller and the page never gains
 * a horizontal scrollbar.
 */
export function SealPreview({ template, colorMode, offerText, offerTerms }: SealPreviewProps) {
  const values = useMemo(
    () => ({ ...previewValues({ offer_text: offerText, offer_terms: offerTerms }, new Date()), copy: "1", of: "1" }),
    [offerText, offerTerms],
  );
  const fill = (text: string) => text.replace(PLACEHOLDER, (_, name: string) => values[name as keyof typeof values] ?? "");
  const ink = (color: "black" | "red" | undefined) =>
    color === "red" && colorMode === "black_red" ? "text-brand-red" : "text-brand-black";

  const preview = useSealPreview();
  const [png, setPng] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const render = () => {
    const request = ++requestRef.current;
    setError(null);
    preview.mutate(
      { template, color_mode: colorMode, offer_text: offerText, offer_terms: offerTerms },
      {
        onSuccess: (result) => {
          if (request !== requestRef.current) return;
          setPng(result.png_base64);
          setWarnings(result.warnings ?? []);
        },
        onError: (e) => {
          if (request !== requestRef.current) return;
          setError(e.message);
        },
      },
    );
  };

  // Re-render on the relay shortly after the owner stops typing.
  const background = template.background_png_base64 ?? null;
  const backgroundRef = useRef({ background, version: 0 });
  if (backgroundRef.current.background !== background) {
    backgroundRef.current = { background, version: backgroundRef.current.version + 1 };
  }
  const signature = JSON.stringify([templateWithoutBackground(template), backgroundRef.current.version, colorMode, offerText, offerTerms]);
  useEffect(() => {
    const timer = setTimeout(render, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const canvas = sealCanvas(template);
  const portrait = canvas.orientation === "portrait";
  const labelStyle = { width: inches(canvas.width), height: inches(canvas.height) };
  // Portrait: each preview column is exactly the strip's width, captions wrap under it.
  const block = portrait ? "shrink-0 space-y-2" : "space-y-2";
  const blockStyle = portrait ? { width: labelStyle.width } : undefined;

  return (
    <div className={portrait ? "flex flex-wrap gap-x-6 gap-y-5" : "space-y-5"}>
      <div className={block} style={blockStyle}>
        <p className="text-sm font-semibold text-foreground">Layout</p>
        <div className="overflow-x-auto pb-2">
          <div
            role="img"
            aria-label="Seal layout sketch at real size"
            data-orientation={canvas.orientation}
            className="relative shrink-0 overflow-hidden rounded-sm border border-border bg-brand-white shadow-sm"
            style={labelStyle}
          >
            {template.background_png_base64 && (
              <img
                src={`data:image/png;base64,${template.background_png_base64}`}
                alt=""
                className="absolute inset-0 h-full w-full object-fill"
                style={colorMode === "black" ? { filter: "grayscale(1)" } : undefined}
              />
            )}
            {template.elements.map((el, i) =>
              el.type === "text" ? (
                <div
                  key={i}
                  data-testid={`sketch-element-${i}`}
                  className={`absolute flex overflow-hidden leading-tight ${ink(el.color)} ${el.font === "bold" ? "font-bold" : "font-normal"} ${
                    el.valign === "top" ? "items-start" : el.valign === "bottom" ? "items-end" : "items-center"
                  } ${el.wrap === false ? "whitespace-nowrap" : "whitespace-pre-wrap break-words"}`}
                  style={{
                    left: inches(el.x),
                    top: inches(el.y),
                    width: inches(el.w),
                    height: inches(el.h),
                    fontSize: inches(el.font_size),
                    fontFamily: "'Liberation Sans', Arial, Helvetica, sans-serif",
                  }}
                >
                  <span className="block w-full" style={{ textAlign: el.align ?? "left" }}>
                    {fill(el.text)}
                  </span>
                </div>
              ) : (
                <div
                  key={i}
                  data-testid={`sketch-element-${i}`}
                  className={`absolute flex items-center justify-center border-2 border-dashed border-current ${ink(el.color)}`}
                  style={{ left: inches(el.x), top: inches(el.y), width: inches(el.size), height: inches(el.size) }}
                >
                  <QrCode className="h-1/2 w-1/2" aria-hidden />
                </div>
              ),
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          A quick sketch with sample order values. Fonts and QR codes are exact only in the printer preview.
        </p>
      </div>

      <div className={block} style={blockStyle}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-foreground">Printer preview</p>
          <Button type="button" variant="outline" size="sm" className="h-11 sm:h-9" onClick={render} disabled={preview.isPending}>
            <RefreshCw className={`mr-2 h-4 w-4 ${preview.isPending ? "animate-spin" : ""}`} aria-hidden />
            {preview.isPending ? "Rendering" : "Refresh"}
          </Button>
        </div>
        <div className="overflow-x-auto pb-2">
          {png ? (
            <img
              src={`data:image/png;base64,${png}`}
              alt="Seal as the printer will render it, at real size"
              className="block max-w-none rounded-sm border border-border"
              style={labelStyle}
            />
          ) : (
            <div
              className="flex items-center justify-center rounded-sm border border-dashed border-border bg-muted px-4 text-center text-xs text-muted-foreground"
              style={labelStyle}
            >
              {preview.isPending ? "Asking the kitchen printer relay for a preview" : "The printer preview appears here."}
            </div>
          )}
        </div>
        {error && (
          <p className="text-xs text-status-danger">
            Could not get a printer preview: {error}. The layout sketch above still reflects your edits.
          </p>
        )}
        {warnings.length > 0 && (
          <ul className="space-y-1 text-xs text-status-warning">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">Rendered by the kitchen relay without printing. Nothing comes out of the printer.</p>
      </div>
    </div>
  );
}
